import type {
  ApolloError,
  BaseQueryOptions,
  MutationTuple,
  OperationVariables,
  QueryResult,
} from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { useErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { FormattedError, type ResponseHandlerOptions } from '@models/error';

import { has } from '@utils/has';

import type { Exact } from 'generated/types';

import { useSnackbar } from './useSnackbar';

type ResponseHandlerError = ApolloError | ReadonlyArray<GraphQLFormattedError>;

type IApiQueryHook<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TParams = any,
> = (...params: TParams[]) => QueryResult<TData, TVariables>;

type IApiMutationHook<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TParams = any,
> = (...params: TParams[]) => MutationTuple<TData, TVariables>;

export function useResponseHandler(
  options: ResponseHandlerOptions = {
    handlerType: 'boundary',
  }
) {
  const { handlerType, snackbarMsgId } = options;
  const { showBoundary } = useErrorBoundary();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  /**
   * Show a formatted error
   * @param error The error to show
   * @param id Intl message key used to get a specific error message
   */
  const showError = (error: FormattedError | undefined, id?: string) => {
    if (!error) {
      return;
    } else if (
      typeof error.code !== 'string' ||
      typeof error.message !== 'string'
    ) {
      const fallbackMessage =
        id || snackbarMsgId
          ? formatMessage({ id: `errorMessage.${id || snackbarMsgId}` })
          : formatMessage({ id: 'errorMessage.error' });
      const fallbackCode = formatMessage({ id: 'errorMessage.issue' });
      error = new FormattedError(fallbackCode, fallbackMessage);
    }

    if (handlerType === 'boundary') {
      showBoundary(error);
    } else {
      enqueueSnackbar({
        message:
          id || snackbarMsgId
            ? formatMessage({
                id: `errorMessage.${id || snackbarMsgId}`,
                defaultMessage: formatMessage({ id: 'errorMessage.default' }),
              })
            : (error.message as string),
        variant: 'error',
      });
    }
  };

  /**
   * Handle an error response
   * @param error The error to handle
   * @param id Intl message key used to get a specific error message
   */
  const handleError = (error?: ResponseHandlerError, id?: string) => {
    const parsedError = parseError(error);
    if (!parsedError) {
      return;
    }

    showError(parsedError, id);
  };

  const handleSuccess = (id?: string) => {
    if (handlerType === 'snackbar' && (id || snackbarMsgId)) {
      enqueueSnackbar({
        message: formatMessage({
          id: `successMessage.${id || snackbarMsgId}`,
          defaultMessage: formatMessage({ id: 'successMessage.default' }),
        }),
        variant: 'success',
      });
    }
  };

  /**
   * Parse a graphql error into {code, message}. Useful for custom handling of specific API responses.
   * @param error Apollo graphql client error
   * @returns Parsed error message, or null
   */
  const parseError = (error?: ResponseHandlerError): FormattedError | null => {
    if (!error) {
      return null;
    }
    let graphqlError: GraphQLFormattedError | null = null;
    let formattedError: FormattedError | null = null;
    if (Array.isArray(error)) {
      graphqlError =
        (error as ReadonlyArray<GraphQLFormattedError>).at(0) ?? null;
    } else {
      graphqlError = (error as ApolloError).graphQLErrors.at(0) ?? null;
    }
    if (!graphqlError) {
      return formattedError;
    }
    // Check for custom formatted error
    if (
      has(graphqlError, 'message') &&
      has(graphqlError, 'code') &&
      typeof graphqlError.code === 'string'
    ) {
      formattedError = new FormattedError(
        graphqlError.code,
        graphqlError.message
      );
    } else if (
      graphqlError.extensions &&
      has(graphqlError.extensions, 'code') &&
      typeof graphqlError.extensions.code === 'string'
    ) {
      formattedError = new FormattedError(
        graphqlError.extensions.code,
        graphqlError.message
      );
    }
    return formattedError;
  };

  return {
    handleError,
    handleSuccess,
    parseError,
    showBoundary,
    enqueueSnackbar,
  };
}

export function withQueryResponseHandler<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TOptions extends BaseQueryOptions<TVariables> = BaseQueryOptions<TVariables>,
>(hook: IApiQueryHook<TData, Exact<TVariables>, TOptions>) {
  return (...params: Parameters<typeof hook>) => {
    const { handleError } = useResponseHandler();
    const response = hook(...params);
    const { error } = response;

    if (error) {
      handleError(error);
    }
    return response;
  };
}

export function withMutationResponseHandler<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(hook: IApiMutationHook<TData, Exact<TVariables>>, snackbarMsgId = 'default') {
  return (...params: Parameters<typeof hook>): ReturnType<typeof hook> => {
    const { handleError, handleSuccess } = useResponseHandler({
      handlerType: 'snackbar',
      snackbarMsgId,
    });

    const [mutate, response] = hook(...params);

    async function mutateWithErrorHandling(
      ...params: Parameters<typeof mutate>
    ) {
      const response = await mutate(...params);

      const { data, errors } = response;
      if (errors) {
        handleError(errors);
      }
      if (data && !errors) {
        handleSuccess();
      }
      return response;
    }
    return [mutateWithErrorHandling, response];
  };
}
